import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useEffect, useState } from "react";

import { ApiError, apiFetch } from "../../api/client";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "../../components/ui";
import {
  type InviteCreateBody,
  type InviteRow,
  type MemberRow,
  TEAM_INVITES_KEY,
  freePlanCapMessage,
  readFreePlanCapLimit,
} from "./types";

// ─── Invite member dialog ─────────────────────────────────────────────

/** #2061 — the placeholder row an invite is bound to (People → row → Invite). */
export type InviteTarget = Pick<MemberRow, "id" | "display_name_in_team" | "tier">;

export function InviteMemberDialog({
  teamId,
  open,
  onOpenChange,
  member = null,
}: {
  teamId: string;
  // `accountId` is retained on the prop contract (still passed by
  // `MembersSidebar`) but no longer consumed here — the outbound auth
  // header is injected centrally by `apiFetch` via the TokenSource
  // (#773 Phase 1), so the dialog stopped threading `authHeaders`.
  accountId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * #2061 — set when the dialog is opened from a placeholder row. The
   * request then carries `member_id`, so accepting the invite attaches
   * the person's account to THAT row (#1722) instead of adding a second,
   * empty row beside it. Omitted for the team-level *Invite by email*.
   */
  member?: InviteTarget | null;
}) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"member" | "co_lead" | "lead">("member");
  const [error, setError] = useState<string | null>(null);
  // Issue #462 — the invite endpoint shares the same 402 free-plan
  // envelope as the manual-add endpoint. When the cap fires we flip
  // this flag so the calm cap copy renders alongside a factual
  // "contact us" note (#1294 removed the Billing surface the note used
  // to link to). Kept as a sibling boolean (rather than piggybacking
  // on `error`) so the type contract on the error renderer stays
  // `string | null`.
  const [planCapHit, setPlanCapHit] = useState(false);
  // Brief 25 / issue 005 Phase 2 — when the route returns a 201 with
  // `notification.status === "failed"`, we keep the dialog open and
  // surface a calm inline failure block (the dialog's focal component
  // when this state is active). The lead can re-trigger delivery via
  // the inline "Resend invite" button without re-typing the address —
  // closes the silent-swallow loop diagnosed in Brief 26.
  const [deliveryFailure, setDeliveryFailure] = useState<{
    reason: string;
    email: string;
  } | null>(null);

  useEffect(() => {
    if (!open) return;
    setEmail("");
    setRole("member");
    setError(null);
    setPlanCapHit(false);
    setDeliveryFailure(null);
  }, [open]);

  const create = useMutation({
    mutationFn: async (body: InviteCreateBody) =>
      apiFetch<InviteRow>(`/api/teams/${teamId}/invites`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: async (data, variables) => {
      // Always refresh the pending-invites list — the row is persisted
      // regardless of delivery outcome, so the lead can revoke or
      // re-trigger from the inline list.
      await queryClient.invalidateQueries({ queryKey: TEAM_INVITES_KEY(teamId) });
      const notification = data.notification;
      if (notification && notification.status === "failed") {
        setDeliveryFailure({
          reason: notification.reason ?? "The email service couldn't accept the message.",
          email: variables.email,
        });
        return;
      }
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      // Issue #462 — invite + manual-add share the same 402 free-plan
      // envelope. Render the calm cap message + "contact us" note
      // instead of the generic "Request failed (402)" string the
      // default `ApiError.detail` getter falls back to.
      const capLimit = readFreePlanCapLimit(err);
      if (capLimit !== null) {
        setError(freePlanCapMessage(capLimit));
        setPlanCapHit(true);
        return;
      }
      setError(
        err instanceof ApiError
          ? err.detail
          : err instanceof Error
            ? err.message
            : "Couldn't send invite",
      );
      setPlanCapHit(false);
    },
  });

  // #2061 — bind the request to the placeholder row when there is one.
  const withTarget = (body: InviteCreateBody): InviteCreateBody =>
    member ? { ...body, member_id: member.id } : body;

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setPlanCapHit(false);
    setDeliveryFailure(null);
    if (!email.trim() || !email.includes("@")) {
      setError("Enter a valid email address.");
      return;
    }
    create.mutate(withTarget({ email: email.trim(), role }));
  };

  const onResend = () => {
    if (!deliveryFailure) return;
    setError(null);
    setPlanCapHit(false);
    setDeliveryFailure(null);
    create.mutate(withTarget({ email: deliveryFailure.email, role }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite a member</DialogTitle>
          <DialogDescription>
            {member
              ? "They'll receive an email with a link to accept. Accepting signs them in to this row: their name, grade and roster stay as they are."
              : "They'll receive an email with a link to accept. Pending invites surface below the members list."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          {member ? (
            <p
              className="rounded-md border border-border bg-bg-subtle px-3 py-2 text-sm text-fg"
              data-testid="invite-target-line"
            >
              Inviting <span className="font-semibold">{member.display_name_in_team}</span>
              {member.tier ? <span className="text-fg-muted"> · {member.tier}</span> : null}
            </p>
          ) : null}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invite-email">Email address</Label>
            <Input
              id="invite-email"
              type="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="rota.member@example.com"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invite-role">Role</Label>
            <select
              id="invite-role"
              value={role}
              onChange={(e) => setRole(e.target.value as "member" | "co_lead" | "lead")}
              className="h-9 rounded-md border border-border bg-bg-subtle px-2 text-sm text-fg focus-visible:border-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent"
            >
              <option value="member">member</option>
              <option value="co_lead">co_lead</option>
              <option value="lead">lead</option>
            </select>
          </div>
          {error ? (
            <p role="alert" className="text-sm text-danger" data-testid="invite-error">
              {error}
            </p>
          ) : null}
          {planCapHit ? (
            <p className="text-sm text-fg-subtle" data-testid="invite-plan-cap-cta">
              Contact us to raise this workspace's member limit.
            </p>
          ) : null}
          {deliveryFailure ? (
            <div
              role="alert"
              data-testid="invite-delivery-failure"
              className="flex flex-col gap-2 rounded-md border border-warning/40 bg-warning/10 p-3"
            >
              <p className="text-sm font-medium text-fg">
                The invite is saved, but the email didn't reach{" "}
                <span className="font-semibold">{deliveryFailure.email}</span>.
              </p>
              <p className="text-xs text-fg-subtle">
                Upstream said: <span className="italic">{deliveryFailure.reason}</span>
              </p>
              <p className="text-xs text-fg-subtle">
                We'll keep the invite on your pending list. You can try again, or ask your workspace
                admin to set up a verified sender domain.
              </p>
              <div>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={onResend}
                  disabled={create.isPending}
                  data-testid="invite-resend-button"
                >
                  {create.isPending ? "Resending…" : "Resend invite"}
                </Button>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={create.isPending}
            >
              {deliveryFailure ? "Close" : "Cancel"}
            </Button>
            <Button type="submit" disabled={create.isPending || deliveryFailure !== null}>
              {create.isPending ? "Sending…" : "Send invite"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
